import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { ApiService } from './api.service';

/**
 * Asks the server whether an identifier is still free.
 *
 * Both questions live on one endpoint family because they are the same question
 * asked by two forms, and because the answer has to be the one the save will give.
 * The server compares exactly what it compares on write — nothing is re-derived
 * here — so a field that goes green is a field that will save.
 *
 * `excludeId` is the record being edited. It is what makes an edit form usable:
 * without it, opening a saved record and re-typing the value it already holds
 * reports a clash with itself, and the user is left unable to save a record they
 * never changed.
 */
@Injectable({ providedIn: 'root' })
export class AvailabilityService extends ApiService {
  /** True when no other patient holds this ID. */
  public isPatientIdAvailable(
    patientId: string,
    excludePatientId?: number | string | null,
  ): Observable<boolean> {
    return this.request({
      path: 'availability/patient-id',
      method: 'GET',
      query: this.query('patientId', patientId, 'excludePatientId', excludePatientId),
    }).pipe(map((response) => !!response?.available));
  }

  /** True when no other staff account holds this address. */
  public isEmailAvailable(
    email: string,
    excludeUserId?: number | string | null,
  ): Observable<boolean> {
    return this.request({
      path: 'availability/email',
      method: 'GET',
      query: this.query('email', email, 'excludeUserId', excludeUserId),
    }).pipe(map((response) => !!response?.available));
  }

  /**
   * An absent exclusion is left out of the query string rather than sent empty:
   * HttpParams stringifies whatever it is given, and `excludeUserId=undefined`
   * would reach the server as the literal word.
   */
  private query(
    valueKey: string,
    value: string,
    excludeKey: string,
    excludeId?: number | string | null,
  ): Record<string, string> {
    const query: Record<string, string> = { [valueKey]: value };
    if (excludeId !== null && excludeId !== undefined && `${excludeId}` !== '') {
      query[excludeKey] = `${excludeId}`;
    }
    return query;
  }
}
